import { useMemo } from 'react';

import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { FieldOwnerState } from '@mui/x-date-pickers';
import { DateRangePicker, LocalizationProvider, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { CentroCustoAutoComplete } from 'components/autocompletes/CentroCustoAutoComplete';
import { CentroDistribuicaoAutoComplete } from 'components/autocompletes/CentroDistribuicaoAutoComplete';
import { CentroPedidoAutoComplete } from 'components/autocompletes/CentroPedidoAutoComplete';
import { ControleEstoqueAutoComplete } from 'components/autocompletes/ControleEstoqueAutoComplete';
import { DepositoAutoComplete } from 'components/autocompletes/DepositoAutoComplete';
import { GeraNotaDebitoAutoComplete } from 'components/autocompletes/GeraNotaDebitoAutoComplete';
import { TabelaPrecoAutoComplete } from 'components/autocompletes/TabelaPrecoAutoComplete';
import { TipoGradeAutoComplete } from 'components/autocompletes/TipoGradeAutoComplete';
import { TipoPedidoAutoComplete } from 'components/autocompletes/TipoPedidoAutoComplete';
import { BbarForm } from 'components/docked/BbarForm';
import { CissNumberField, CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import { ConfigPedidoTiposLojas } from 'pages/compra/configuracao-pedido/components';
import { useConfigPedidoDirty, useMutateDadosBasicos, useQueryDadosBasicos } from 'pages/compra/configuracao-pedido/hooks';
import { initialDadosBasicosValues } from 'pages/compra/configuracao-pedido/types';

export function ConfigPedidoDadosBasicosForm() {
    const { data, isFetching } = useQueryDadosBasicos();
    const { onSubmit, isLoading } = useMutateDadosBasicos();
    const { focusError } = useFocusErrorFormik();

    useLoaderContext(isLoading || isFetching);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                dsConfigPedido: Yup.string().required(),
                dtFimVigencia: Yup.string().required(),
                dtInicioVigencia: Yup.string().required(),
                vlMinimo: Yup.number().required(),
                fgEnviaEmailPendenteGrade: Yup.boolean().required(),
                fgSincronizaTerceiros: Yup.boolean().required(),
                tipoControleEstoquePedido: Yup.object().required(),
                tipoPedido: Yup.object().required(),
                tipoGrade: Yup.object().required(),
                deposito: Yup.object().required(),
                centroDistribuicao: Yup.object().required(),
                centroPedido: Yup.object().required(),
                centroCusto: Yup.object().required(),
                tabelaPreco: Yup.object().required(),
                pedidoTipoNotaDebito: Yup.object().required(),
                fgLojaMetaAtingida: Yup.boolean().required(),
                fgLojaFaixaRoyalties: Yup.boolean().required(),
                fgAtivo: Yup.boolean().required(),
                coefNotaDebito: Yup.string().nullable(),
                peRoyalties: Yup.string().nullable(),
                fgGeraNotaDebito: Yup.boolean().nullable(),
                fgVisivel: Yup.boolean().required(),
                configPedidoOperacoes: Yup.array().of(
                    Yup.object().shape({
                        operacao: Yup.object().required(),
                    }),
                ),
            }),
        [],
    );

    const formik = useFormik({
        initialValues: data || initialDadosBasicosValues,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit,
        enableReinitialize: true,
    });

    const { errors, values, setFieldValue, dirty, submitForm } = formik;

    focusError(errors);

    useConfigPedidoDirty(dirty);

    return (
        <FormikProvider value={formik}>
            <Block>
                <SubTitle label="Dados básicos" />

                <Row alignItems="center" sx={{ flexDirection: 'row' }}>
                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Nome da configuração"
                            name="dsConfigPedido"
                            error={errors.dsConfigPedido}
                            value={values.dsConfigPedido}
                            onChange={(e) => setFieldValue('dsConfigPedido', e.target.value)}
                        />
                    </Grid>

                    <Grid>
                        <FormControlLabel
                            label="Ativo"
                            name="fgAtivo"
                            control={<Checkbox />}
                            checked={values.fgAtivo}
                            onChange={(e, checked) => setFieldValue('fgAtivo', checked)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <ControleEstoqueAutoComplete
                            name="tipoControleEstoquePedido"
                            value={values.tipoControleEstoquePedido}
                            error={errors.tipoControleEstoquePedido}
                            onChange={(e, value) => setFieldValue('tipoControleEstoquePedido', value)}
                        />
                    </Grid>

                    <Grid sx={{ width: '100%', flex: 1 }}>
                        <LocalizationProvider
                            adapterLocale={ptBR}
                            dateAdapter={AdapterDateFns}
                            localeText={{ start: 'Vigência início', end: 'Vigência fim' }}
                        >
                            <DateRangePicker
                                value={[
                                    values.dtInicioVigencia ? new Date(values.dtInicioVigencia) : null,
                                    values.dtFimVigencia ? new Date(values.dtFimVigencia) : null,
                                ]}
                                onChange={([startDate, endDate]) => {
                                    setFieldValue('dtInicioVigencia', startDate);
                                    setFieldValue('dtFimVigencia', endDate);
                                }}
                                slots={{ field: MultiInputDateRangeField }}
                                slotProps={{
                                    field: { dateSeparator: 'até' },
                                    textField: ({ position }: FieldOwnerState & { position?: 'start' | 'end' }) => ({
                                        autoComplete: 'off',
                                        name: position === 'start' ? 'dtInicioVigencia' : 'dtFimVigencia',
                                        error: position === 'start' ? Boolean(errors.dtInicioVigencia) : Boolean(errors.dtFimVigencia),
                                        helperText: position === 'start' ? errors.dtInicioVigencia : errors.dtFimVigencia,
                                    }),
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <TipoPedidoAutoComplete
                            name="tipoPedido"
                            error={errors.tipoPedido}
                            value={values.tipoPedido}
                            onChange={(e, value) => setFieldValue('tipoPedido', value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <TipoGradeAutoComplete
                            name="tipoGrade"
                            error={errors.tipoGrade}
                            value={values.tipoGrade}
                            onChange={(e, value) => setFieldValue('tipoGrade', value)}
                        />
                    </Grid>
                </Row>

                <Row alignItems="center" sx={{ flexDirection: 'row' }}>
                    <Grid sx={{ flex: 1 }}>
                        <CissNumberField
                            label="Valor Mínimo"
                            name="vlMinimo"
                            value={values.vlMinimo}
                            error={errors.vlMinimo}
                            onChange={(e) => setFieldValue('vlMinimo', Number(e.target.value))}
                            inputType="decimal"
                            preventEmptyField={false}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <FormControlLabel
                            label="Envia e-mail pendente de grade"
                            name="fgEnviaEmailPendenteGrade"
                            control={<Checkbox />}
                            checked={values.fgEnviaEmailPendenteGrade}
                            onChange={(e, checked) => setFieldValue('fgEnviaEmailPendenteGrade', checked)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <FormControlLabel
                            label="Integrar pedidos automaticamente"
                            name="fgSincronizaTerceiros"
                            control={<Checkbox />}
                            checked={values.fgSincronizaTerceiros}
                            onChange={(e, checked) => setFieldValue('fgSincronizaTerceiros', checked)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Observação"
                            name="dsObservacao"
                            value={values.dsObservacao}
                            onChange={(e) => setFieldValue('dsObservacao', e.target.value)}
                            multiline
                            error={errors.dsObservacao}
                            minRows={5}
                        />
                    </Grid>
                </Row>
            </Block>

            <Block>
                <SubTitle label="Dados do estoque" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <DepositoAutoComplete
                            name="deposito"
                            error={errors.deposito}
                            value={values.deposito}
                            onChange={(e, value) => setFieldValue('deposito', value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CentroPedidoAutoComplete
                            name="centroPedido"
                            error={errors.centroPedido}
                            value={values.centroPedido}
                            onChange={(e, value) => setFieldValue('centroPedido', value)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CentroDistribuicaoAutoComplete
                            name="centroDistribuicao"
                            error={errors.centroDistribuicao}
                            value={values.centroDistribuicao}
                            onChange={(e, value) => setFieldValue('centroDistribuicao', value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CentroCustoAutoComplete
                            name="centroCusto"
                            error={errors.centroCusto}
                            value={values.centroCusto}
                            onChange={(e, value) => setFieldValue('centroCusto', value)}
                        />
                    </Grid>
                </Row>
            </Block>

            <Block>
                <SubTitle label="Tabela de preço" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <TabelaPrecoAutoComplete
                            label=""
                            name="tabelaPreco"
                            error={errors.tabelaPreco}
                            value={values.tabelaPreco}
                            onChange={(e, value) => setFieldValue('tabelaPreco', value)}
                        />
                    </Grid>
                </Row>
            </Block>

            <Block>
                <SubTitle label="Dados de transação" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <GeraNotaDebitoAutoComplete
                            name="pedidoTipoNotaDebito"
                            error={errors.pedidoTipoNotaDebito}
                            value={values.pedidoTipoNotaDebito}
                            onChange={(e, value) => setFieldValue('pedidoTipoNotaDebito', value)}
                        />
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <CissNumberField
                            label="Coeficiente ND"
                            name="peRoyalties"
                            maxValue={100}
                            inputType="decimal"
                            value={values.peRoyalties}
                            error={errors.peRoyalties}
                            onChange={(e) => setFieldValue('peRoyalties', Number(e.target.value))}
                        />
                    </Grid>
                </Row>

                <ConfigPedidoTiposLojas
                    configPedidoOperacoes={values.configPedidoOperacoes}
                    idConfigPedido={values.idConfigPedido}
                    setFieldValue={setFieldValue}
                />
            </Block>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
